import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * Password input with a show/hide toggle (Design Specification Section 12.2, WCAG 3.3.8: no puzzle or
 * transcription, paste and password managers allowed — this is a plain text/password input, never a
 * multi-box or masked-only field). `aria-pressed` on the toggle per AC-ACCT-3.
 */
export default function PasswordField({ id, label, value, onChange, autoComplete, error, hint, required, maxLength }) {
  const [show, setShow] = useState(false);
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;

  return (
    <div>
      <label htmlFor={id} className="text-overline text-zinc-500 mb-1 block">
        {label}{required ? <span aria-hidden="true"> *</span> : <span className="normal-case tracking-normal"> (optional)</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          maxLength={maxLength}
          aria-invalid={error ? "true" : "false"}
          aria-describedby={[error ? errId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined}
          className="input-brutal pr-14"
          data-testid={`field-${id}`}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-pressed={show}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#0A2540] w-8 h-8 flex items-center justify-center"
          data-testid={`toggle-${id}`}
        >
          {show ? <EyeOff className="w-5 h-5" aria-hidden="true" /> : <Eye className="w-5 h-5" aria-hidden="true" />}
        </button>
      </div>
      {hint && <p id={hintId} className="text-xs text-zinc-500 mt-1">{hint}</p>}
      {error && <p id={errId} role="alert" className="text-sm text-red-700 mt-1">{error}</p>}
    </div>
  );
}
